/**
 * useBrandFonts — the active Brand Kit's fonts (heading + body, deduped) as an
 * email-safe {family, fallback} chain, PLUS the side-effect that actually makes
 * them render.
 *
 * Two jobs, both extracted verbatim from ``SignatureSettings`` (the original
 * home of this logic) so every email-authoring surface — the signature editor,
 * the inbox reply body, the send-email draft preview — offers the SAME brand
 * fonts from ONE place (DRY):
 *
 *   1. Derive ``{ family, fallback }[]`` from ``spec.style.heading_font`` +
 *      ``body_font`` (deduped). ``fallback`` is an email-safe web font appended
 *      to the CSS chain — custom brand fonts do NOT render in the RECIPIENT's
 *      email client, so a serviceable fallback (Georgia / Arial) rides along.
 *   2. Register the real font FILES in the browser via the FontFace API,
 *      fetched credentialed from ``/brand-kits/_effective/font/{role}`` and
 *      added with ``document.fonts.add`` — a cross-origin ``@font-face`` would
 *      not send the auth cookie, so the editor would otherwise show only the
 *      fallback. Best-effort: a missing file just leaves the fallback in place.
 *
 * When there's no active kit / no fonts the hook returns ``[]`` — callers show
 * only their built-in generics, never crash.
 */

import { getEnv } from "@ando/core";
import * as React from "react";

import { useEffectiveBrandKit } from "./useBrandKits";

/** A Brand Kit font + its email-safe fallback (appended to the CSS chain). */
export interface BrandFont {
  /** Real family (Brand Kit — custom upload or the default Ando font). */
  readonly family: string;
  /** Email-safe fallback in the chain (recipients without the font see this). */
  readonly fallback: string;
}

/** Famiglie "serif-like" → fallback Georgia; tutto il resto → Arial. */
const SERIF_HINTS = [
  "gelasio",
  "georgia",
  "times",
  "playfair",
  "merriweather",
  "lora",
  "serif",
];

/** Map a family name to an email-safe fallback (serif-like → Georgia, else Arial). */
export function fontFallback(family: string): string {
  const f = family.toLowerCase();
  return SERIF_HINTS.some((h) => f.includes(h)) ? "Georgia" : "Arial";
}

/**
 * The active Brand Kit's fonts as a ``{ family, fallback }[]`` chain, with the
 * real font files registered in the browser as a side effect. Empty when there
 * is no active kit or it declares no fonts.
 */
export function useBrandFonts(): readonly BrandFont[] {
  const kitQ = useEffectiveBrandKit();

  // Font del Brand Kit (upload custom inclusi), dedup heading/body.
  const brandFonts = React.useMemo<BrandFont[]>(() => {
    const st = kitQ.data?.spec.style;
    if (!st) return [];
    const fams = [...new Set([st.heading_font, st.body_font].filter(Boolean))];
    return fams.map((family) => ({ family, fallback: fontFallback(family) }));
  }, [kitQ.data]);

  // Registra i font del kit nel browser (FontFace API, fetch con cookie) così
  // l'editor li MOSTRA davvero — un CSS @font-face cross-origin non manderebbe
  // le credenziali. Best-effort: se il file non c'è, l'editor usa il fallback.
  React.useEffect(() => {
    let cancelled = false;
    const roles: Array<["heading" | "body", string | undefined]> = [
      ["heading", kitQ.data?.spec.style.heading_font],
      ["body", kitQ.data?.spec.style.body_font],
    ];
    for (const [role, family] of roles) {
      if (!family || document.fonts.check(`13px '${family}'`)) continue;
      void (async () => {
        try {
          const res = await fetch(
            `${getEnv().apiUrl}/brand-kits/_effective/font/${role}`,
            { credentials: "include" },
          );
          if (!res.ok || cancelled) return;
          const face = new FontFace(family, await res.arrayBuffer());
          await face.load();
          if (!cancelled) document.fonts.add(face);
        } catch {
          /* best-effort — fallback font in preview */
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [kitQ.data]);

  return brandFonts;
}
